import { formatMoney } from '@lib/money'
import { GetPaymentOwedType } from '@orbit/api-types'

/**
 * A signed currency amount: positive = owed TO the subject; negative = the subject owes.
 */
export interface CurrencyBalance {
    currencyCode: string
    amount: number
}

/**
 * One outstanding balance from the perspective of a specific user.
 * `direction === 'owe'` means the subject owes `otherUserId`.
 * `direction === 'owed'` means `otherUserId` owes the subject.
 */
export interface OutstandingBalance {
    otherUserId: number
    currencyCode: string
    amount: number
    direction: 'owe' | 'owed'
}

/**
 * One row inside a member's expanded breakdown on the Balances page,
 * from that member's perspective: positive = `otherUserId` owes the member.
 */
export interface MemberBreakdownItem {
    otherUserId: number
    currencyCode: string
    amount: number
}

export interface MemberBalanceGroup {
    userId: number
    balancesPerCurrency: CurrencyBalance[]
    breakdown: MemberBreakdownItem[]
}

/**
 * Tagged union describing what the balance summary banner / tile should display.
 * Keeps copy-generation logic out of the component layer so the same shape can
 * drive the trip overview tile, the Expenses page banner, and the PayUps page banner.
 */
export type BalanceBannerSummary =
    | { kind: 'settled' }
    | {
          kind: 'singleDirection'
          direction: 'owe' | 'owed'
          primary: CurrencyBalance
          otherCurrencyCount: number
      }
    | { kind: 'mixed'; entries: CurrencyBalance[] }

const isUserInvolved = (payment: GetPaymentOwedType, userId: number): boolean =>
    payment.fromUserId === userId || payment.toUserId === userId

export const computeNetBalanceForUser = (
    paymentsOwed: GetPaymentOwedType[],
    userId: number,
): CurrencyBalance[] => {
    const totals = new Map<string, number>()

    for (const payment of paymentsOwed) {
        if (payment.toUserId === userId) {
            totals.set(payment.currencyCode, (totals.get(payment.currencyCode) ?? 0) + payment.amount)
        } else if (payment.fromUserId === userId) {
            totals.set(payment.currencyCode, (totals.get(payment.currencyCode) ?? 0) - payment.amount)
        }
    }

    return Array.from(totals.entries())
        .filter(([, amount]) => amount !== 0)
        .map(([currencyCode, amount]) => ({ currencyCode, amount }))
        .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
}

export const summarizeBalanceForBanner = (
    balances: CurrencyBalance[],
): BalanceBannerSummary => {
    const nonZero = balances.filter(b => b.amount !== 0)

    if (nonZero.length === 0) {
        return { kind: 'settled' }
    }

    const allPositive = nonZero.every(b => b.amount > 0)
    const allNegative = nonZero.every(b => b.amount < 0)

    if (allPositive || allNegative) {
        const sorted = [...nonZero].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
        const [primarySigned, ...rest] = sorted
        return {
            kind: 'singleDirection',
            direction: allPositive ? 'owed' : 'owe',
            primary: { currencyCode: primarySigned.currencyCode, amount: Math.abs(primarySigned.amount) },
            otherCurrencyCount: rest.length,
        }
    }

    return {
        kind: 'mixed',
        entries: [...nonZero].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)),
    }
}

export const computeOutstandingForUser = (
    paymentsOwed: GetPaymentOwedType[],
    userId: number,
): OutstandingBalance[] => {
    const items: OutstandingBalance[] = paymentsOwed
        .filter(p => isUserInvolved(p, userId) && p.amount !== 0)
        .map(p => ({
            otherUserId: p.fromUserId === userId ? p.toUserId : p.fromUserId,
            currencyCode: p.currencyCode,
            amount: p.amount,
            direction: p.fromUserId === userId ? 'owe' : 'owed',
        }))

    return items.sort((a, b) => {
        if (a.direction !== b.direction) {
            return a.direction === 'owe' ? -1 : 1
        }
        if (b.amount !== a.amount) {
            return b.amount - a.amount
        }
        return a.otherUserId - b.otherUserId
    })
}

export const groupBalancesByMember = (
    paymentsOwed: GetPaymentOwedType[],
    memberIds: number[],
): MemberBalanceGroup[] => {
    return memberIds.map(userId => {
        const breakdown: MemberBreakdownItem[] = paymentsOwed
            .filter(p => isUserInvolved(p, userId))
            .map(p => ({
                otherUserId: p.fromUserId === userId ? p.toUserId : p.fromUserId,
                currencyCode: p.currencyCode,
                amount: p.toUserId === userId ? p.amount : -p.amount,
            }))

        const totals = new Map<string, number>()
        for (const item of breakdown) {
            totals.set(item.currencyCode, (totals.get(item.currencyCode) ?? 0) + item.amount)
        }

        const balancesPerCurrency: CurrencyBalance[] = Array.from(totals.entries())
            .filter(([, amount]) => amount !== 0)
            .map(([currencyCode, amount]) => ({ currencyCode, amount }))
            .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))

        return { userId, balancesPerCurrency, breakdown }
    })
}

/**
 * Convert a `BalanceBannerSummary` into the user-facing copy string used by the
 * Expenses page banner and the PayUps page banner.
 */
export const formatBalanceBannerCopy = (summary: BalanceBannerSummary): string => {
    if (summary.kind === 'settled') {
        return "You're settled up ✓"
    }
    if (summary.kind === 'singleDirection') {
        const verb = summary.direction === 'owe' ? 'You owe' : "You're owed"
        const amountStr = formatMoney(summary.primary.amount, summary.primary.currencyCode)
        const showCode = summary.otherCurrencyCount > 0
        const codeStr = showCode ? ` ${summary.primary.currencyCode.toUpperCase()}` : ''
        const moreStr = summary.otherCurrencyCount > 0 ? ` +${summary.otherCurrencyCount} more` : ''
        return `${verb} ${amountStr}${codeStr}${moreStr}`
    }
    return summary.entries
        .map(entry => {
            const sign = entry.amount >= 0 ? '+' : '-'
            const amountStr = formatMoney(Math.abs(entry.amount), entry.currencyCode)
            return `${sign}${amountStr} ${entry.currencyCode.toUpperCase()}`
        })
        .join(', ')
}

export const partitionBreakdownByInvolvement = (
    breakdown: MemberBreakdownItem[],
    currentUserId: number,
): { involvingUser: MemberBreakdownItem[]; otherBalances: MemberBreakdownItem[] } => {
    const involvingUser: MemberBreakdownItem[] = []
    const otherBalances: MemberBreakdownItem[] = []
    for (const item of breakdown) {
        if (item.otherUserId === currentUserId) {
            involvingUser.push(item)
        } else {
            otherBalances.push(item)
        }
    }
    return { involvingUser, otherBalances }
}
